import { useCallback, useEffect, useState } from "react";
import { Alert, PageHeader, Spinner, Stat } from "../ds";
import { WgPeerTable } from "../components/wireguard/WgPeerTable";
import { WgServerPanel } from "../components/wireguard/WgServerPanel";
import { api, errorText, type WgOverview, type WgPeerInput, type WgServerConfig } from "../lib/api";
import { useWgStream } from "../lib/hooks/useWgStream";

// Der WireGuard-Konzentrator: wen der Server hereinlässt, und was davon
// tatsächlich ankommt (docs/wireguard-server.md).
//
// Hier stand einmal zusätzlich eine Tabelle mit den Uplinks aller Router, aus
// deren Snapshot-Cache gespeist. Sie ist raus: seit der Server selbst misst, ist
// die Selbstauskunft eines Routers die schwächere Zahl — sie ist so alt wie der
// letzte Refresh, während der Handshake am Server von jetzt ist. Bezahlt wurde
// sie mit 23 HTTP-Aufrufen beim Öffnen (Fleet-Liste plus einer je Router).
//
// Damit lädt die Seite nur noch ihre eigene Übersicht und hält sie über einen
// SSE-Stream aktuell. Kein Router wird dafür angefasst.

export function WireguardPage() {
  const [wg, setWg] = useState<WgOverview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // GENAU EIN Abonnement. Stand hier zweimal und öffnete zwei Streams je
  // Zuschauer — doppelte Last auf einem Dienst, dessen offene Streams schon
  // einmal einen Neustart blockiert haben (docs/deploy.md).
  useWgStream(setWg);

  useEffect(() => {
    let abgebrochen = false;
    api
      .getWireguard()
      .then((uebersicht) => {
        if (!abgebrochen) setWg(uebersicht);
      })
      .catch((e: unknown) => {
        if (!abgebrochen) setError(errorText(e));
      });
    return () => {
      abgebrochen = true;
    };
  }, []);

  const schreiben = useCallback(async (aktion: () => Promise<WgOverview>) => {
    setBusy(true);
    setError(null);
    try {
      // Jede schreibende Antwort ist die vollständige Übersicht — ein zweiter
      // Abruf wäre nicht nur überflüssig, sondern ein zweiter Stand.
      setWg(await aktion());
    } catch (e: unknown) {
      setError(errorText(e));
      throw e;
    } finally {
      setBusy(false);
    }
  }, []);

  const aktiv = wg?.peers.filter((p) => p.enabled).length ?? 0;
  const stumm = wg?.peers.filter((p) => p.enabled && p.latestHandshakeSecs === null).length ?? 0;

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="WireGuard"
        subtitle="Der Konzentrator, an dem die Flotte hängt — Bestand und Messung."
      />

      {error && <Alert variant="danger">{error}</Alert>}

      {wg === null ? (
        <div className="flex justify-center p-10">
          <Spinner />
        </div>
      ) : (
        <>
          <WgServerPanel
            data={wg}
            onSave={(patch: Partial<WgServerConfig>) =>
              schreiben(() => api.updateWgServer(patch)).then(() => undefined)
            }
          />

          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Stat label="Peers" value={wg.peers.length} iconName="key" />
            <Stat label="Aktiv" value={aktiv} iconName="shield" />
            <Stat label="Ohne Handshake" value={stumm} iconName="timer" />
            <Stat label="Unbekannte Schlüssel" value={wg.unknown.length} iconName="close" />
          </div>

          <WgPeerTable
            data={wg}
            busy={busy}
            onSave={(id, peer: WgPeerInput) =>
              schreiben(() => (id ? api.updateWgPeer(id, peer) : api.createWgPeer(peer))).then(
                () => undefined,
              )
            }
            onDelete={(peer) => schreiben(() => api.deleteWgPeer(peer.id)).then(() => undefined)}
          />
        </>
      )}
    </div>
  );
}
